import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import {
  PaperPlaneTilt, Envelope, EnvelopeOpen, CursorClick, WarningCircle,
  Signature, TrendUp, MapPin, ArrowSquareOut, Flask, Trophy, UserCircle, ClockCountdown, Phone, ArrowClockwise, DownloadSimple,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const STATUS_FR = {
  sent: "Envoyé", delivered: "Délivré", opened: "Ouvert", clicked: "Cliqué",
  bounced: "Rejeté", delayed: "Différé", complained: "Plainte",
  completed: "Signé", declined: "Refusé", voided: "Annulé", created: "Créé",
};
const STATUS_TONE = {
  opened: "bg-emerald-50 text-emerald-700 border-emerald-200",
  clicked: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-blue-50 text-blue-700 border-blue-200",
  sent: "bg-slate-100 text-slate-600 border-slate-200",
  bounced: "bg-red-50 text-red-700 border-red-200",
  declined: "bg-red-50 text-red-700 border-red-200",
  voided: "bg-red-50 text-red-700 border-red-200",
  complained: "bg-red-50 text-red-700 border-red-200",
};

function StatCard({ icon: Icon, label, value, sub, tone = "text-foreground", testid }) {
  return (
    <div className="bg-white border border-border p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 text-muted-foreground">
        <Icon size={16} weight="bold" />
        <span className="overline">{label}</span>
      </div>
      <div className={`font-mono text-3xl font-bold ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function FunnelBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="mb-3" data-testid={`funnel-${label}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium">{label}</span>
        <span className="font-mono text-muted-foreground">{count} · {pct}%</span>
      </div>
      <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

const fmtDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
};

const CALL_COLORS = { rdv: "#059669", rappeler: "#D97706", email: "#0284C7", repondeur: "#64748B", refus: "#DC2626", autre: "#94A3B8" };

function CallsCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/reports/calls?days=${days}`).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  if (!data) return null;
  return (
    <div className="bg-white border border-border p-5" data-testid="calls-card">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-2 overline text-primary">
          <Phone size={16} weight="fill" /> Suivi des appels
        </span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="calls-period"
          className="border border-border px-2 py-1 text-xs outline-none bg-white">
          <option value={7}>7 jours</option>
          <option value={30}>30 jours</option>
          <option value={90}>90 jours</option>
          <option value={365}>1 an</option>
        </select>
      </div>
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-3xl font-head font-bold" data-testid="calls-total">{data.total}</span>
        <span className="text-sm text-muted-foreground">appel(s) · {data.rdv_rate}% de RDV obtenus</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {data.order.map((k) => (
          <div key={k} className="border border-border px-3 py-2" data-testid={`calls-outcome-${k}`}>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="w-2 h-2 rounded-full" style={{ background: CALL_COLORS[k] }} />{data.labels[k]}
            </div>
            <div className="text-xl font-mono font-bold" style={{ color: CALL_COLORS[k] }}>{data.counts[k] || 0}</div>
          </div>
        ))}
      </div>
      {data.recent.length > 0 && (
        <div className="border-t border-border pt-3">
          <div className="overline text-muted-foreground mb-2">Derniers appels</div>
          <div className="space-y-1">
            {data.recent.slice(0, 6).map((r, i) => (
              <div key={`${r.company_id}-${i}`} className="flex items-center justify-between text-xs py-1" data-testid="call-recent-row">
                <span className="font-medium truncate max-w-[240px]">{r.company_nom || "—"}</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white" style={{ background: CALL_COLORS[r.outcome] }}>
                  {data.labels[r.outcome]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SeqStep({ num, color, title, when, extra, children, last }) {
  return (
    <div className="flex gap-3" data-testid={`seq-step-${num}`}>
      <div className="flex flex-col items-center">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: color }}>{num}</div>
        {!last && <div className="w-px flex-1 bg-border mt-1" />}
      </div>
      <div className="flex-1 pb-2">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-head font-bold text-sm">{title}</div>
            <div className="text-[11px] text-muted-foreground font-mono">{when}</div>
          </div>
          {extra}
        </div>
        {children}
      </div>
    </div>
  );
}

function HistoryCard() {
  const [weeks, setWeeks] = useState([]);
  useEffect(() => { api.get("/reports/weekly-history?weeks=8").then((r) => setWeeks(r.data.weeks || [])).catch(() => {}); }, []);
  if (!weeks.length) return null;
  const hasData = weeks.some((w) => w.calls > 0 || w.rdv > 0);
  return (
    <div className="bg-white border border-border p-5" data-testid="history-card">
      <div className="flex items-center gap-2 mb-4">
        <TrendUp size={16} weight="fill" className="text-primary" />
        <span className="overline text-primary">Tendance appels / RDV (8 semaines)</span>
      </div>
      {!hasData ? (
        <p className="text-sm text-muted-foreground py-4">Pas encore d'historique d'appels. Vos statistiques apparaîtront semaine après semaine.</p>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={weeks} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94A3B8" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94A3B8" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 0 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="calls" name="Appels" fill="#002FA7" radius={[2, 2, 0, 0]} />
            <Bar dataKey="rdv" name="RDV" fill="#059669" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function GoalBar({ label, done, goal, pct, color }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs font-mono text-muted-foreground"><b style={{ color }}>{done}</b> / {goal}</span>
      </div>
      <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function GoalsCard() {
  const [g, setG] = useState(null);
  const [edit, setEdit] = useState(false);
  const [calls, setCalls] = useState(50);
  const [rdv, setRdv] = useState(10);
  const load = () => api.get("/reports/weekly-goals").then((r) => { setG(r.data); setCalls(r.data.goals.calls); setRdv(r.data.goals.rdv); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/reports/weekly-goals", { calls: Number(calls), rdv: Number(rdv) }); toast.success("Objectifs mis à jour"); setEdit(false); load(); }
    catch { toast.error("Erreur"); }
  };
  if (!g) return null;
  return (
    <div className="bg-white border border-border p-5" data-testid="goals-card">
      <div className="flex items-center justify-between mb-4">
        <span className="flex items-center gap-2 overline text-primary"><Trophy size={16} weight="fill" /> Objectifs de la semaine</span>
        <button onClick={() => setEdit((v) => !v)} data-testid="goals-edit" className="text-xs font-semibold text-primary hover:underline">{edit ? "Fermer" : "Modifier"}</button>
      </div>
      {edit ? (
        <div className="flex items-end gap-3 flex-wrap" data-testid="goals-edit-fields">
          <label className="text-xs">Appels / semaine
            <input type="number" min={0} value={calls} onChange={(e) => setCalls(e.target.value)} data-testid="goal-calls-input" className="w-24 border border-border px-2 py-1.5 text-sm outline-none mt-1 block" /></label>
          <label className="text-xs">RDV / semaine
            <input type="number" min={0} value={rdv} onChange={(e) => setRdv(e.target.value)} data-testid="goal-rdv-input" className="w-24 border border-border px-2 py-1.5 text-sm outline-none mt-1 block" /></label>
          <button onClick={save} data-testid="goals-save" className="px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800">Enregistrer</button>
        </div>
      ) : (
        <div className="space-y-4">
          <GoalBar label="Appels passés" done={g.calls_done} goal={g.goals.calls} pct={g.calls_pct} color="#002FA7" />
          <GoalBar label="RDV obtenus" done={g.rdv_done} goal={g.goals.rdv} pct={g.rdv_pct} color="#059669" />
          <p className="text-[11px] text-muted-foreground">Depuis lundi. {g.calls_pct >= 100 && g.rdv_pct >= 100 ? "🎯 Objectifs atteints, bravo !" : "Continuez comme ça 💪"}</p>
        </div>
      )}
    </div>
  );
}

function RecallsCard() {
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/reports/recalls").then((r) => setData(r.data)).catch(() => {});
    api.get("/reports/recalls/digest/config").then((r) => setDigest(r.data)).catch(() => {});
  }, []);
  const toggleDigest = async () => {
    const next = !digest?.enabled;
    setDigest((d) => ({ ...d, enabled: next }));
    try { await api.put("/reports/recalls/digest/config", { enabled: next }); toast.success(next ? "Récap matinal activé" : "Récap matinal désactivé"); }
    catch { toast.error("Erreur"); }
  };
  const sendNow = async () => {
    try { const { data } = await api.post("/reports/recalls/digest/send"); toast.success(data.sent ? `Récap envoyé (${data.count} cabinet(s))` : (data.reason || "Rien à envoyer")); }
    catch { toast.error("Erreur d'envoi"); }
  };
  if (!data) return null;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—");
  return (
    <div className="bg-white border border-border p-5" data-testid="recalls-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Phone size={16} weight="fill" className="text-amber-600" />
          <span className="overline text-primary">À rappeler aujourd'hui</span>
          <span className="ml-1 text-xs font-mono bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full" data-testid="recalls-due-count">{data.due_count}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="digest-toggle" title="Recevoir un récap chaque matin par email">
            <input type="checkbox" checked={!!digest?.enabled} onChange={toggleDigest} className="accent-primary" />
            Récap matinal par email
          </label>
          <button onClick={sendNow} data-testid="digest-send-now" className="text-xs font-semibold text-primary hover:underline">Recevoir maintenant</button>
        </div>
      </div>
      {data.due.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Aucun rappel dû. 🎉 Marquez un appel « À rappeler » (avec une date) pour le retrouver ici.</p>
      ) : (
        <div className="divide-y divide-border">
          {data.due.map((r) => (
            <button key={r.company_id} onClick={() => navigate(`/cabinet/${r.company_id}`)} data-testid={`recall-row-${r.company_id}`}
              className="w-full flex items-center justify-between py-2.5 text-left hover:bg-secondary px-1">
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{r.company_nom || "—"}</div>
                {r.note && <div className="text-xs text-muted-foreground truncate max-w-[380px]">{r.note}</div>}
              </div>
              <div className="text-right shrink-0 ml-3">
                <div className="text-xs font-mono text-amber-700">{fmt(r.recall_date)}</div>
                <div className="text-[10px] text-muted-foreground">dernier appel {fmt(r.last_call)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {data.upcoming.length > 0 && (
        <div className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
          {data.upcoming.length} rappel(s) programmé(s) plus tard.
        </div>
      )}
    </div>
  );
}

function RelanceCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/outreach/relance/config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const set = (k, v) => setCfg((s) => ({ ...s, [k]: v }));
  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/outreach/relance/config", patch || {
        enabled: cfg.enabled, delay_days: Number(cfg.delay_days) || 4,
        subject: cfg.subject, body: cfg.body, from_email: cfg.from_email,
        second_relance: cfg.second_relance, delay_days_2: Number(cfg.delay_days_2) || 10,
        subject_2: cfg.subject_2, body_2: cfg.body_2,
        initial_subject: cfg.initial_subject, initial_body: cfg.initial_body,
        call_relance: cfg.call_relance, call_relance_delay: Number(cfg.call_relance_delay) || 3,
      });
      setCfg((s) => ({ ...s, ...data }));
      toast.success("Relance automatique enregistrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const toggle = () => save({ enabled: !cfg.enabled });
  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/outreach/relance/run");
      toast.success(data.error ? data.error : `${data.sent || 0} relance(s) envoyée(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setRunning(false); }
  };
  return (
    <div className="bg-white border border-border p-5" data-testid="relance-card">
      <div className="flex items-center justify-between mb-1">
        <span className="flex items-center gap-2 overline text-primary">
          <ClockCountdown size={16} weight="fill" /> Séquence d'emails automatique
        </span>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="relance-toggle">
          <input type="checkbox" checked={!!cfg.enabled} onChange={toggle} className="accent-primary" />
          <span className={cfg.enabled ? "text-emerald-700 font-semibold" : "text-muted-foreground"}>{cfg.enabled ? "Relances activées" : "Relances désactivées"}</span>
        </label>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Éditez chaque étape. La relance ne part qu'aux cibles qui n'ont <b>pas ouvert</b> l'email précédent, et <b>s'arrête</b> dès qu'une cible ouvre, clique ou entre au pipeline.
        {cfg.enabled ? " Le contrôle tourne automatiquement chaque jour." : ""}
        {typeof cfg.pending === "number" && <> {cfg.pending} cible(s) en attente.</>}
      </p>

      {/* Expéditeur global */}
      <label className="text-xs block mb-4 max-w-xs">
        <span className="overline text-muted-foreground">Expéditeur des relances</span>
        <select value={cfg.from_email} onChange={(e) => set("from_email", e.target.value)}
          data-testid="relance-sender" className="w-full border border-border px-2 py-1.5 text-sm outline-none mt-1 bg-white">
          <option value="paul@ai-assurances.com">paul@ai-assurances.com</option>
          <option value="amine.idrissi@ai-assurances.com">amine.idrissi@ai-assurances.com</option>
        </select>
      </label>

      <div className="space-y-4">
        {/* Étape 0 : email initial */}
        <SeqStep num="1" color="#002FA7" title="Email initial" when="Jour 0 — envoyé depuis le Deal Sourcing">
          <label className="text-xs block">
            <span className="overline text-muted-foreground">Objet (modèle par défaut)</span>
            <input value={cfg.initial_subject || ""} onChange={(e) => set("initial_subject", e.target.value)}
              data-testid="seq-initial-subject" className="w-full border border-border px-2 py-1.5 text-sm outline-none mt-1" />
          </label>
          <label className="text-xs block mt-2">
            <span className="overline text-muted-foreground">Message (pré-rempli au lancement d'une campagne)</span>
            <textarea value={cfg.initial_body || ""} onChange={(e) => set("initial_body", e.target.value)} rows={5}
              data-testid="seq-initial-body" className="w-full border border-border px-2 py-1.5 text-xs outline-none mt-1 font-mono" />
          </label>
        </SeqStep>

        {/* Étape 1 : relance 1 */}
        <SeqStep num="2" color="#D97706" title="Relance 1" when={`Jour +${cfg.delay_days} après l'email initial`}>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="text-xs">
              <span className="overline text-muted-foreground">Délai (jours)</span>
              <input type="number" min={1} max={60} value={cfg.delay_days} onChange={(e) => set("delay_days", e.target.value)}
                data-testid="relance-delay" className="w-full border border-border px-2 py-1.5 text-sm outline-none mt-1" />
            </label>
            <label className="text-xs sm:col-span-2">
              <span className="overline text-muted-foreground">Objet</span>
              <input value={cfg.subject} onChange={(e) => set("subject", e.target.value)}
                data-testid="relance-subject" className="w-full border border-border px-2 py-1.5 text-sm outline-none mt-1" />
            </label>
          </div>
          <label className="text-xs block mt-2">
            <span className="overline text-muted-foreground">Message (variables {"{civilite_nom}"}, {"{ville}"})</span>
            <textarea value={cfg.body} onChange={(e) => set("body", e.target.value)} rows={4}
              data-testid="relance-body" className="w-full border border-border px-2 py-1.5 text-xs outline-none mt-1 font-mono" />
          </label>
        </SeqStep>

        {/* Étape 2 : relance 2 */}
        <SeqStep num="3" color="#64748B" title="Relance 2" when={cfg.second_relance ? `Jour +${cfg.delay_days_2} après la relance 1` : "Désactivée"}
          extra={<label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="relance2-toggle">
            <input type="checkbox" checked={!!cfg.second_relance} onChange={(e) => set("second_relance", e.target.checked)} className="accent-primary" />
            {cfg.second_relance ? "Activée" : "Activer"}
          </label>}>
          {cfg.second_relance && (
            <div data-testid="relance2-fields">
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="text-xs">
                  <span className="overline text-muted-foreground">Délai (jours)</span>
                  <input type="number" min={1} max={90} value={cfg.delay_days_2} onChange={(e) => set("delay_days_2", e.target.value)}
                    data-testid="relance2-delay" className="w-full border border-border px-2 py-1.5 text-sm outline-none mt-1" />
                </label>
                <label className="text-xs sm:col-span-2">
                  <span className="overline text-muted-foreground">Objet</span>
                  <input value={cfg.subject_2} onChange={(e) => set("subject_2", e.target.value)}
                    data-testid="relance2-subject" className="w-full border border-border px-2 py-1.5 text-sm outline-none mt-1" />
                </label>
              </div>
              <label className="text-xs block mt-2">
                <span className="overline text-muted-foreground">Message</span>
                <textarea value={cfg.body_2} onChange={(e) => set("body_2", e.target.value)} rows={4}
                  data-testid="relance2-body" className="w-full border border-border px-2 py-1.5 text-xs outline-none mt-1 font-mono" />
              </label>
            </div>
          )}
        </SeqStep>

        {/* Étape 4 : appel de relance */}
        <SeqStep num="4" color="#DC2626" title="Appel de relance" last
          when={cfg.call_relance ? `Jour +${cfg.call_relance_delay} après la dernière relance email` : "Désactivé"}
          extra={<label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="call-relance-toggle">
            <input type="checkbox" checked={!!cfg.call_relance} onChange={(e) => set("call_relance", e.target.checked)} className="accent-primary" />
            {cfg.call_relance ? "Activé" : "Activer"}
          </label>}>
          {cfg.call_relance && (
            <div data-testid="call-relance-fields">
              <p className="text-xs text-muted-foreground mb-2">
                Si l'email reste sans réponse, la cible est ajoutée automatiquement à « À rappeler aujourd'hui » (aucun email envoyé).
                {typeof cfg.pending_calls === "number" && <> {cfg.pending_calls} appel(s) à planifier.</>}
              </p>
              <label className="text-xs block max-w-[180px]">
                <span className="overline text-muted-foreground">Délai (jours)</span>
                <input type="number" min={1} max={60} value={cfg.call_relance_delay} onChange={(e) => set("call_relance_delay", e.target.value)}
                  data-testid="call-relance-delay" className="w-full border border-border px-2 py-1.5 text-sm outline-none mt-1" />
              </label>
            </div>
          )}
        </SeqStep>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button onClick={() => save()} disabled={saving} data-testid="relance-save"
          className="px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
          {saving ? "Enregistrement…" : "Enregistrer la séquence"}
        </button>
        <button onClick={runNow} disabled={running} data-testid="relance-run-now"
          className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary disabled:opacity-50">
          {running ? "Envoi…" : `Lancer les relances dues (${cfg.pending || 0})`}
        </button>
      </div>
    </div>
  );
}

function DemoABBar({ hasDemo, onReload }) {
  const [busy, setBusy] = useState(false);
  const run = async (create) => {
    setBusy(true);
    try {
      if (create) {
        const { data } = await api.post("/reports/campaigns/ab/demo");
        toast.success(`Démo créée — gagnant : variante ${data.winner}. Alerte email ${data.alert_sent ? "envoyée" : "non envoyée"}.`);
      } else {
        await api.delete("/reports/campaigns/ab/demo");
        toast.success("Campagne de démo supprimée");
      }
      onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 mb-3" data-testid="ab-demo-bar">
      <button data-testid="ab-demo-create" onClick={() => run(true)} disabled={busy}
        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-violet-300 text-violet-700 bg-violet-50 px-3 py-1.5 hover:bg-violet-100 disabled:opacity-50">
        <Flask size={13} weight="fill" /> {busy ? "…" : "Générer une démo A/B"}
      </button>
      {hasDemo && (
        <button data-testid="ab-demo-delete" onClick={() => run(false)} disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border px-3 py-1.5 hover:bg-secondary disabled:opacity-50">
          <ArrowClockwise size={13} /> Retirer la démo
        </button>
      )}
    </div>
  );
}

function ABTestingSection({ ab, onReload }) {
  const campaigns = ab.campaigns || [];
  const bySender = ab.by_sender || [];
  const hasDemo = campaigns.some((c) => (c.name || "").includes("DÉMO"));

  if (campaigns.length === 0 && bySender.length === 0) {
    return (
      <div className="bg-white border border-border p-5" data-testid="ab-testing-empty">
        <div className="flex items-center gap-2 mb-3">
          <Flask size={16} className="text-primary" weight="fill" />
          <span className="overline">A/B Testing des campagnes</span>
        </div>
        <p className="text-sm text-muted-foreground py-4 text-center">
          Aucune campagne email trackée pour l'instant. Lancez une campagne (avec variantes A/B) depuis le Sourcing,
          puis configurez le webhook Resend en production pour voir les taux d'ouverture et de clics.
        </p>
        <DemoABBar hasDemo={hasDemo} onReload={onReload} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ab-testing-section">
      <DemoABBar hasDemo={hasDemo} onReload={onReload} />
      {/* Comparaison par campagne */}
      {campaigns.length > 0 && (
        <div className="bg-white border border-border" data-testid="ab-campaigns">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Flask size={16} className="text-primary" weight="fill" />
            <span className="font-head font-bold text-sm">A/B Testing par campagne</span>
          </div>
          <div className="divide-y divide-border">
            {campaigns.map((c) => (
              <div key={c.id} className="p-5" data-testid={`ab-campaign-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-semibold text-sm">{c.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{fmtDay(c.date)}</div>
                  </div>
                  {c.ab && (
                    <span className="text-[10px] uppercase tracking-wide bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 rounded-full">
                      A/B
                    </span>
                  )}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b border-border">
                      <th className="text-left overline py-2">Variante</th>
                      <th className="text-left overline py-2">Expéditeur</th>
                      <th className="text-right overline py-2 px-2">Envoyés</th>
                      <th className="text-right overline py-2 px-2">Délivrés</th>
                      <th className="text-right overline py-2 px-2">Ouverture</th>
                      <th className="text-right overline py-2 px-2">Clics</th>
                      <th className="text-right overline py-2 px-2">Rejets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.variants.map((v) => {
                      const isWinner = c.winner && v.variant === c.winner;
                      return (
                        <tr key={v.variant} className={isWinner ? "bg-emerald-50/60" : ""} data-testid={`ab-variant-${c.id}-${v.variant}`}>
                          <td className="py-2 font-semibold">
                            <span className="inline-flex items-center gap-1.5">
                              {v.variant.toUpperCase()}
                              {isWinner && <Trophy size={13} weight="fill" className="text-emerald-600" data-testid="ab-winner-icon" />}
                            </span>
                          </td>
                          <td className="py-2 text-xs text-muted-foreground font-mono truncate max-w-[180px]">{v.from}</td>
                          <td className="py-2 px-2 text-right font-mono">{v.sent}</td>
                          <td className="py-2 px-2 text-right font-mono">{v.delivered}</td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-700 font-semibold">{v.open_rate}%</td>
                          <td className="py-2 px-2 text-right font-mono text-sky-600">{v.click_rate}%</td>
                          <td className="py-2 px-2 text-right font-mono text-red-600">{v.bounced || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {c.ab && c.winner && (
                  <div className="mt-3 text-xs flex items-center gap-1.5 text-emerald-700" data-testid={`ab-winner-${c.id}`}>
                    <Trophy size={13} weight="fill" />
                    Variante gagnante : <span className="font-semibold uppercase">{c.winner}</span> (meilleur taux d'ouverture)
                  </div>
                )}
                {c.ab && !c.winner && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    Pas encore de gagnant — en attente d'ouvertures (webhook Resend requis en production).
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cumul par expéditeur */}
      {bySender.length > 0 && (
        <div className="bg-white border border-border" data-testid="ab-by-sender">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <UserCircle size={16} className="text-primary" weight="fill" />
            <span className="font-head font-bold text-sm">Performance cumulée par expéditeur</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                <th className="text-left overline py-2.5 px-5">Expéditeur</th>
                <th className="text-right overline py-2.5 px-3">Envoyés</th>
                <th className="text-right overline py-2.5 px-3">Ouverts</th>
                <th className="text-right overline py-2.5 px-3">Cliqués</th>
                <th className="text-right overline py-2.5 px-3">Rejets</th>
                <th className="text-right overline py-2.5 px-5">Taux d'ouverture</th>
              </tr>
            </thead>
            <tbody>
              {bySender.map((s) => (
                <tr key={s.from} className="border-b border-border last:border-0 hover:bg-secondary" data-testid={`sender-row-${s.from}`}>
                  <td className="py-2.5 px-5 font-mono text-xs">{s.from}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{s.sent}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{s.opened}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{s.clicked}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-red-600">{s.bounced}</td>
                  <td className="py-2.5 px-5 text-right font-mono font-semibold text-emerald-700">{s.open_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Campaigns() {
  const [data, setData] = useState(null);
  const [ab, setAb] = useState(null);
  const [err, setErr] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reports/campaigns").then((r) => setData(r.data)).catch(() => setErr(true));
    api.get("/reports/campaigns/ab").then((r) => setAb(r.data)).catch(() => setAb({ campaigns: [], by_sender: [] }));
  }, []);

  if (err) return <div className="p-8 text-muted-foreground">Erreur de chargement du suivi des campagnes.</div>;
  if (!data) return <div className="p-8 text-muted-foreground">Chargement…</div>;

  const { email, signatures, by_department, timeline, totals } = data;

  const exportXlsx = async () => {
    try {
      const res = await api.get("/reports/export/contacts.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cabinets_contactes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch { toast.error("Erreur d'export"); }
  };

  return (
    <div>
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <h1 className="font-head font-bold text-xl flex items-center gap-2">
          <PaperPlaneTilt size={20} className="text-primary" weight="fill" /> Suivi des campagnes
        </h1>
        <div className="flex items-center gap-4">
          <div className="text-xs text-muted-foreground font-mono hidden sm:block">
            {totals.targets} cible(s) approchée(s) · {totals.overall_conversion}% de conversion
          </div>
          <button onClick={exportXlsx} data-testid="export-contacts-xlsx"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-border hover:bg-secondary">
            <DownloadSimple size={15} /> Exporter Excel
          </button>
        </div>
      </header>

      <div className="p-8 space-y-6">
        <RecallsCard />
        <GoalsCard />
        <HistoryCard />
        <RelanceCard />
        <CallsCard />
        {/* KPIs principaux */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="kpi-targets" icon={PaperPlaneTilt} label="Cibles approchées" value={totals.targets}
            sub="emails ou signatures envoyés" tone="text-primary" />
          <StatCard testid="kpi-open-rate" icon={EnvelopeOpen} label="Taux d'ouverture" value={`${email.open_rate}%`}
            sub={`${email.opened}/${email.sent} emails ouverts`} tone="text-emerald-700" />
          <StatCard testid="kpi-loi-signed" icon={Signature} label="LOI signées" value={signatures.loi.signed}
            sub={`${signatures.loi.sent} envoyée(s) · ${signatures.loi.sign_rate}%`} tone="text-foreground" />
          <StatCard testid="kpi-acquired" icon={TrendUp} label="Acquisitions" value={totals.acquired}
            sub={`${totals.in_pipeline} deal(s) en pipeline`} tone="text-primary" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Entonnoir emails */}
          <div className="bg-white border border-border p-5" data-testid="email-funnel">
            <div className="flex items-center gap-2 mb-4">
              <Envelope size={16} className="text-primary" weight="fill" />
              <span className="overline">Entonnoir emails (Resend)</span>
            </div>
            {email.sent === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Aucun email de campagne envoyé pour l'instant. Lancez une campagne depuis « Prospection ciblée ».
              </p>
            ) : (
              <>
                <FunnelBar label="Envoyés" count={email.sent} total={email.sent} color="#94A3B8" />
                <FunnelBar label="Délivrés" count={email.delivered} total={email.sent} color="#002FA7" />
                <FunnelBar label="Ouverts" count={email.opened} total={email.sent} color="#059669" />
                <FunnelBar label="Cliqués" count={email.clicked} total={email.sent} color="#0EA5E9" />
                {email.bounced > 0 && <FunnelBar label="Rejetés" count={email.bounced} total={email.sent} color="#DC2626" />}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border text-center">
                  <div><div className="font-mono text-lg font-bold text-primary">{email.delivery_rate}%</div><div className="overline text-muted-foreground">Délivrabilité</div></div>
                  <div><div className="font-mono text-lg font-bold text-emerald-700">{email.open_rate}%</div><div className="overline text-muted-foreground">Ouverture</div></div>
                  <div><div className="font-mono text-lg font-bold text-sky-600">{email.click_rate}%</div><div className="overline text-muted-foreground">Clics</div></div>
                </div>
              </>
            )}
          </div>

          {/* Signatures */}
          <div className="bg-white border border-border p-5" data-testid="signatures-block">
            <div className="flex items-center gap-2 mb-4">
              <Signature size={16} className="text-primary" weight="fill" />
              <span className="overline">Signatures électroniques (DocuSign)</span>
            </div>
            {(signatures.loi.sent + signatures.nda.sent) === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Aucun document envoyé en signature. Envoyez une LOI/NDA depuis une fiche cabinet.
              </p>
            ) : (
              <div className="space-y-4">
                {["loi", "nda"].map((k) => {
                  const s = signatures[k];
                  return (
                    <div key={k} data-testid={`sig-${k}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold uppercase">{k}</span>
                        <span className="font-mono text-muted-foreground">
                          {s.signed}/{s.sent} signée(s) · {s.sign_rate}%
                        </span>
                      </div>
                      <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${s.sign_rate}%` }} />
                      </div>
                      {s.declined > 0 && <div className="text-xs text-red-600 mt-1">{s.declined} refusée(s)/annulée(s)</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* A/B Testing des campagnes */}
        {ab && <ABTestingSection ab={ab} onReload={() => api.get("/reports/campaigns/ab").then((r) => setAb(r.data)).catch(() => {})} />}

        {/* Par département */}
        <div className="bg-white border border-border" data-testid="by-department">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <MapPin size={16} className="text-primary" weight="fill" />
            <span className="font-head font-bold text-sm">Performance par département</span>
          </div>
          {by_department.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">Aucune donnée de campagne par département.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left overline py-2.5 px-5">Département</th>
                  <th className="text-right overline py-2.5 px-3">Emails</th>
                  <th className="text-right overline py-2.5 px-3">Ouverture</th>
                  <th className="text-right overline py-2.5 px-3">LOI env.</th>
                  <th className="text-right overline py-2.5 px-3">LOI signées</th>
                  <th className="text-right overline py-2.5 px-3">Deals</th>
                  <th className="text-right overline py-2.5 px-5">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {by_department.map((r) => (
                  <tr key={r.departement} className="border-b border-border last:border-0 hover:bg-secondary" data-testid={`dept-row-${r.departement}`}>
                    <td className="py-2.5 px-5">
                      <span className="font-semibold font-mono">{r.departement}</span>
                      {r.region && <span className="text-xs text-muted-foreground ml-2">{r.region}</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono">{r.emails}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-emerald-700">{r.open_rate}%</td>
                    <td className="py-2.5 px-3 text-right font-mono">{r.loi_sent}</td>
                    <td className="py-2.5 px-3 text-right font-mono">{r.loi_signed}</td>
                    <td className="py-2.5 px-3 text-right font-mono">{r.deals}</td>
                    <td className="py-2.5 px-5 text-right font-mono font-semibold text-primary">{r.conversion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-white border border-border p-5" data-testid="campaign-timeline">
          <div className="overline text-muted-foreground mb-3">Activité récente</div>
          {timeline.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune activité de campagne enregistrée.</p>
          ) : (
            <div className="space-y-2">
              {timeline.map((t, i) => (
                <div key={`${t.kind}-${t.company_id || "x"}-${t.date || i}`} className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid="timeline-item">
                  {t.kind === "email"
                    ? <Envelope size={16} className="text-primary shrink-0" />
                    : <Signature size={16} className="text-foreground shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.label}</div>
                    <button onClick={() => t.company_id && navigate(`/cabinet/${t.company_id}`)}
                      className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                      {t.company_nom} {t.company_id && <ArrowSquareOut size={11} />}
                    </button>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wide border px-1.5 py-0.5 rounded-full shrink-0 ${STATUS_TONE[t.status] || STATUS_TONE.sent}`}>
                    {STATUS_FR[t.status] || t.status}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono shrink-0 w-24 text-right">{fmtDate(t.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
